import React, { useCallback, useMemo, ReactElement } from 'react';
import Card from '../card/card.component';
import Tooltip from '../tooltip/tooltip.component';
import ActionButton from '../action-button/action-button.component';
import CheckAccess from '../check-access/check-access.component';
import Grid from '../grid/grid.component';
import GridItem from '../grid-item/grid-item.component';
import CloseIcon from '../../icons/close-icon';
import EditIcon from '../../icons/edit-icon';
import FormatService from '../../services/format-service';
import useScreenMobile from '../../hooks/use-screen-mobile';
import useStylesContentHeader from './content-header.styles';

const ContentHeader: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isModal,
    isEditMode,
    withEditModeSwitcher,
    title,
    imageComponent,
    editPermission,
    onCloseModal,
    onSwitchModalEditMode,
    leftBlockComponent,
  } = props;

  const { classes } = useStylesContentHeader();
  const isMobileScreen: boolean = useScreenMobile();

  const editPermissions: string[] = useMemo((): string[] => [editPermission], [editPermission]);

  const withLeftBlock: boolean = !!leftBlockComponent;
  const withTooltip: boolean = isModal;
  const withButtons: boolean = isModal;

  const modalClassName: string | undefined = isModal ? classes.contentHeaderModal : undefined;
  const mainBlockNoLeftClassName: string | undefined = !withLeftBlock
    ? classes.contentHeaderMainBlockNoLeft : undefined;
  const mainBlockModalClassName: string | undefined = isModal ? classes.contentHeaderMainBlockModal : undefined;
  const titleBlockCenteredClassName: string | undefined = !withLeftBlock
    ? classes.contentHeaderTitleBlockCentered : undefined;
  const titleBlockModalClassName: string | undefined = isModal ? classes.contentHeaderTitleBlockModal : undefined;
  const titleBlockModalExtendedClassName: string | undefined = isModal && withEditModeSwitcher && !isMobileScreen
    ? classes.contentHeaderTitleBlockModalExtended : undefined;

  const switchEditMode: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onSwitchModalEditMode) {
      onSwitchModalEditMode();
    }

    event.stopPropagation();
  }, [isEditMode]);

  const closeModal: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    event.stopPropagation();
  }, []);

  return (
    <Card
      withMediumShadow={isModal}
      className={FormatService.formatClassNames(classes.contentHeader, modalClassName)}
    >
      <Grid>
        {
          !!leftBlockComponent && (
            <GridItem isSmallWidth className={classes.contentHeaderLeftBlock}>
              {leftBlockComponent}
            </GridItem>
          )
        }
        <GridItem isFullWidth={!withLeftBlock} isLargeWidth={withLeftBlock}>
          <div
            className={FormatService.formatClassNames(
              classes.contentHeaderMainBlock, mainBlockNoLeftClassName, mainBlockModalClassName,
            )}
          >
            {
              !!imageComponent && (
                <div className={classes.contentHeaderImage}>
                  {imageComponent}
                </div>
              )
            }
            <div
              className={FormatService.formatClassNames(
                classes.contentHeaderTitle, titleBlockCenteredClassName, titleBlockModalClassName,
                titleBlockModalExtendedClassName,
              )}
            >
              {
                withTooltip ? (
                  <Tooltip title={title}>
                    <div>
                      {title}
                    </div>
                  </Tooltip>
                ) : (
                  <div>
                    {title}
                  </div>
                )
              }
            </div>
            {
              withButtons && (
                <div className={classes.contentHeaderButtons}>
                  {
                    withEditModeSwitcher && !isMobileScreen && (
                      <CheckAccess permissions={editPermissions}>
                        <Tooltip title={isEditMode ? 'View' : 'Edit'}>
                          <ActionButton withNoHighlight isActive={isEditMode} onClick={switchEditMode}>
                            <EditIcon />
                          </ActionButton>
                        </Tooltip>
                      </CheckAccess>
                    )
                  }
                  <ActionButton onClick={closeModal}>
                    <CloseIcon />
                  </ActionButton>
                </div>
              )
            }
          </div>
        </GridItem>
      </Grid>
    </Card>
  );
});

export default ContentHeader;
